import type { Client } from "trailbase";
import {
  nameRecordSchema,
  newNameSchema,
  newRatingSchema,
  ratingRecordSchema,
  settingRecordSchema,
} from "../domain/types";
import type {
  NameRecord,
  NewName,
  NewRating,
  RatingRecord,
  SettingRecord,
} from "../domain/types";

const pageLimit = 1024;

const requireUser = (client: Client): string => {
  const user = client.user();
  if (user === undefined) {
    throw new Error("A signed-in user is required.");
  }
  return user.id;
};

const listAll = async <T>(
  client: Client,
  apiName: string,
  parse: (record: unknown) => T,
): Promise<readonly T[]> => {
  const api = client.records<unknown>(apiName);
  const records: T[] = [];
  let cursor: string | undefined;
  do {
    const pagination =
      cursor === undefined
        ? { limit: pageLimit }
        : { limit: pageLimit, cursor };
    const response = await api.list({ pagination });
    for (const record of response.records) {
      records.push(parse(record));
    }
    cursor = response.cursor;
  } while (cursor !== undefined && cursor !== "");
  return records;
};

export const listNames = async (
  client: Client,
): Promise<readonly NameRecord[]> =>
  listAll(client, "names", (record) => nameRecordSchema.parse(record));

export const listRatings = async (
  client: Client,
): Promise<readonly RatingRecord[]> =>
  listAll(client, "ratings", (record) => ratingRecordSchema.parse(record));

export const createName = async (
  client: Client,
  input: NewName,
): Promise<void> => {
  const data = newNameSchema.parse(input);
  const userId = requireUser(client);
  await client
    .records<NewName & { readonly created_by: string }>("names")
    .create({
      ...data,
      created_by: userId,
    });
};

export const updateName = async (
  client: Client,
  id: string,
  input: NewName,
): Promise<void> => {
  const data = newNameSchema.parse(input);
  await client.records<NewName>("names").update(id, data);
};

export const deleteName = async (client: Client, id: string): Promise<void> => {
  await client.records("names").delete(id);
};

export const createRating = async (
  client: Client,
  input: NewRating,
): Promise<void> => {
  const data = newRatingSchema.parse(input);
  requireUser(client);
  await client.records<NewRating>("ratings").create(data);
};

export const updateRating = async (
  client: Client,
  id: string,
  input: NewRating,
): Promise<void> => {
  const data = newRatingSchema.parse(input);
  requireUser(client);
  await client.records<NewRating>("ratings").update(id, data);
};

export const deleteRating = async (
  client: Client,
  id: string,
): Promise<void> => {
  requireUser(client);
  await client.records("ratings").delete(id);
};

export const listSettings = async (
  client: Client,
): Promise<readonly SettingRecord[]> =>
  listAll(client, "settings", (record) => settingRecordSchema.parse(record));

export const upsertSetting = async (
  client: Client,
  key: string,
  value: string,
): Promise<void> => {
  requireUser(client);
  const api = client.records<{ key: string; value: string; updated_by: string }>("settings");
  const existing = await listSettings(client);
  const match = existing.find((s) => s.key === key);
  if (match !== undefined) {
    await api.update(match.id, { key, value, updated_by: requireUser(client) });
  } else {
    await api.create({ key, value, updated_by: requireUser(client) });
  }
};
